/**
 * Shell Command Tokenizer
 *
 * Uses the battle-tested `shell-quote` library to safely tokenize
 * shell commands, handling:
 * - Quoted strings with spaces
 * - Escaped characters
 * - Environment variables
 * - Shell operators (which we filter out)
 */

import { ParseEntry, parse as shellParse } from 'shell-quote';

/**
 * Result of tokenizing a shell command
 */
interface TokenizeResult {
  /** The individual tokens (arguments) */
  tokens: string[];
  /** Whether the command contained shell operators (|, >, etc.) */
  hasOperators: boolean;
  /** Any warnings generated during parsing */
  warnings: string[];
}

/**
 * Type guard for string tokens
 */
const isStringToken = (entry: ParseEntry): entry is string => {
  return typeof entry === 'string';
};

/**
 * Type guard for operator tokens (e.g., { op: '|' })
 */
const isOperatorToken = (entry: ParseEntry): entry is Extract<ParseEntry, { op: unknown }> => {
  return typeof entry === 'object' && entry !== null && 'op' in entry;
};

/**
 * Type guard for glob pattern tokens
 */
const isGlobToken = (entry: ParseEntry): entry is { op: 'glob'; pattern: string } => {
  if (typeof entry !== 'object' || entry === null || !('op' in entry)) return false;
  const obj = entry as { op: string };
  return obj.op === 'glob';
};

/**
 * Tokenizes a shell command string into individual arguments
 *
 * @param command - The shell command to tokenize
 * @returns TokenizeResult with tokens, operator flag, and warnings
 *
 * @example
 * tokenize('claude mcp add --header "My Header: value" myserver')
 * // => { tokens: ['claude', 'mcp', 'add', '--header', 'My Header: value', 'myserver'], hasOperators: false, warnings: [] }
 *
 * @example
 * tokenize('echo "hello world" | grep hello')
 * // => { tokens: ['echo', 'hello world', 'grep', 'hello'], hasOperators: true, warnings: ['Command contains shell operators which will be ignored'] }
 */
export const tokenize = (command: string): TokenizeResult => {
  const tokens: string[] = [];
  const warnings: string[] = [];
  let hasOperators = false;

  try {
    // Pass a custom env handler that preserves variable references as literals.
    // Without this, shell-quote resolves ${secrets.X} and $VAR to empty strings
    // since those variables don't exist in the environment. We want to keep them
    // as-is because they're secret placeholders resolved later by the backend.
    const preserveVars = (varName: string) => `\${${varName}}`;
    const parseWithEnv = shellParse as (
      cmd: string,
      env: (varName: string) => string,
    ) => ParseEntry[];
    const parsed = parseWithEnv(command, preserveVars);

    parsed.forEach((entry) => {
      if (isStringToken(entry)) {
        tokens.push(entry);
      } else if (isOperatorToken(entry)) {
        hasOperators = true;
        // Don't add operators to tokens
      } else if (isGlobToken(entry)) {
        // Treat glob patterns as literal strings
        tokens.push(entry.pattern);
      } else if (typeof entry === 'object' && entry !== null && 'comment' in entry) {
        // Skip comments
      } else {
        // Unknown entry type - try to convert to string
        tokens.push(typeof entry === 'object' ? JSON.stringify(entry) : String(entry));
      }
    });

    if (hasOperators) {
      warnings.push('Command contains shell operators which will be ignored');
    }
  } catch (error) {
    // If shell-quote fails, fall back to simple split
    // This shouldn't happen with well-formed commands
    warnings.push(`Shell parsing failed, using simple tokenization: ${String(error)}`);
    const simpleTokens = command.split(/\s+/).filter(Boolean);
    tokens.push(...simpleTokens);
  }

  return { tokens, hasOperators, warnings };
};

/**
 * Rejoins tokens back into a command string (for debugging/display)
 *
 * @param tokens - Array of tokens to join
 * @returns A shell-safe command string
 */
export const rejoinTokens = (tokens: string[]): string => {
  return tokens
    .map((token) => {
      // Quote tokens that contain spaces or special characters
      if (/[\s"'\\$`!*?#~<>|;&()]/.test(token)) {
        // Escape double quotes inside the token
        const escaped = token.replace(/"/g, '\\"');
        return `"${escaped}"`;
      }
      return token;
    })
    .join(' ');
};
